import React, { useCallback } from 'react';
import { batch } from 'react-redux';
import { TypeUtils } from '@vectord/utils';

import {
  CLASS_TEMPLATE_BULK_UPDATE_TYPES,
  EditLessonForm,
  LECTURE_LESSON_TYPES,
  LESSON_INPUT_OPTIONS,
  LESSON_TYPES,
  PlaylistField,
  setBulkUpdateSyncConfirmModalState,
  setBulkUpdateSyncType,
} from 'features/classCreation';
import AcademyProjectDetailsButton from '../AcademyProjectDetailsButton';
import BulkUpdateSyncButton from '../BulkUpdateSyncButton';
import EditableSection from 'components/EditableSection';
import EditAcademyProjectInput from '../EditAcademyProjectInput';
import Field from 'components/Field';
import LectureDetails from '../LectureDetails';
import LessonChip from '../LessonChip';
import useAppDispatch from 'hooks/useAppDispatch';

export default function EditLectureForm({ academyTopic, classTag = null }) {
  const dispatch = useAppDispatch();

  const {
    lessons,
    playlist_owner: playlistOwner,
    academy_project: academyProject,
  } = academyTopic || {};
  const { value: classTagValue } = classTag || {};

  const isAcademyProjectPresent = TypeUtils.isPresent(academyProject);
  const handleBulkUpdateSyncing = useCallback(
    (bulkUpdateSyncType) => {
      batch(() => {
        dispatch(setBulkUpdateSyncType(bulkUpdateSyncType));
        dispatch(setBulkUpdateSyncConfirmModalState(true));
      });
    },
    [dispatch]
  );

  return (
    <>
      <LectureDetails academyTopic={academyTopic} />
      <Field
        label="Lecture Requirements"
        labelClassName="m-b-xs"
        actions={
          <BulkUpdateSyncButton
            handleOnSync={() =>
              handleBulkUpdateSyncing(CLASS_TEMPLATE_BULK_UPDATE_TYPES.lessons)
            }
          />
        }
      >
        <div className="m-b-s">
          {LECTURE_LESSON_TYPES.map((lessonType) => (
            <LessonChip
              academyTopicId={academyTopic.id}
              lessonType={lessonType}
              title={LESSON_INPUT_OPTIONS[lessonType].label}
              key={lessonType}
            />
          ))}
        </div>
        {lessons.map((lesson) => {
          const { lesson_type: lessonType } = lesson;
          const label = LESSON_INPUT_OPTIONS[lessonType]?.label;
          const isLiveLecture = lessonType === LESSON_TYPES.liveLecture;

          return (
            label && (
              <EditableSection
                canEdit
                title={label}
                titleClassName="hint"
                className="m-b-s"
                key={lesson.id}
              >
                {(editableProps) => (
                  <>
                    <EditLessonForm
                      {...editableProps}
                      academyTopicId={academyTopic.id}
                      value={lesson}
                      label={label}
                      classTagValue={classTagValue}
                      classTagActions={
                        <BulkUpdateSyncButton
                          handleOnSync={() =>
                            handleBulkUpdateSyncing(
                              CLASS_TEMPLATE_BULK_UPDATE_TYPES.classTag
                            )
                          }
                        />
                      }
                    />
                    {isLiveLecture && (
                      <PlaylistField
                        playlistOwner={playlistOwner}
                        actions={
                          <BulkUpdateSyncButton
                            handleOnSync={() =>
                              handleBulkUpdateSyncing(
                                CLASS_TEMPLATE_BULK_UPDATE_TYPES.playlists
                              )
                            }
                          />
                        }
                      />
                    )}
                  </>
                )}
              </EditableSection>
            )
          );
        })}
      </Field>
      <EditableSection
        canEdit={false}
        title="Case Study"
        titleClassName="bold dark"
        className="m-b-s"
        actions={
          <>
            <AcademyProjectDetailsButton
              isAcademyProjectPresent={isAcademyProjectPresent}
            />
            <BulkUpdateSyncButton
              handleOnSync={() =>
                handleBulkUpdateSyncing(
                  CLASS_TEMPLATE_BULK_UPDATE_TYPES.academyProjects
                )
              }
            />
          </>
        }
      >
        {(showDetailsProps) => (
          <EditAcademyProjectInput
            {...showDetailsProps}
            academyTopic={academyTopic}
          />
        )}
      </EditableSection>
    </>
  );
}
